import { Drawer } from 'antd';
import { useEffect, useMemo, useState, Fragment } from 'react';
import dayjs from 'dayjs';
import { uniqBy } from 'lodash';
import { useTranslation } from 'react-i18next';
import Item from './Item';
import { THistoryMeeting } from '@/common/basis/types/database/thistory-meeting';
import { getHistoryMeetingCollectionList } from '@/common/http/meet';
import { isSuccessCode } from '@/common/utils/response';
import MeetingDetail from '@/components/MeetingDetail';
import { getRepeatOptions } from '@/common/utils/select-options';

function App(props: {
  currentHistoryIndex: number;
  historyList: THistoryMeeting.Item[];
  isOpenCollectionList: boolean;
  setIsOpenCollectionList: (isOpenCollectionList: boolean) => void;
}) {
  const isOpenCollectionList = props.isOpenCollectionList;
  const setIsOpenCollectionList = props.setIsOpenCollectionList;
  const historyList = props.historyList;
  const currentHistoryIndex = props.currentHistoryIndex;

  const item = useMemo(() => historyList[currentHistoryIndex], [currentHistoryIndex, historyList]);
  const roomId = useMemo(() => item?.roomId ?? '', [item?.roomId]);
  const roomName = useMemo(() => item?.roomName ?? '', [item?.roomName]);
  const repeatType = useMemo(() => item?.repeatType, [item?.repeatType]);
  const { t } = useTranslation();
  const [collectionList, setCollectionList] = useState<THistoryMeeting.Item[]>([]);
  const tipText = useMemo(
    () => getRepeatOptions().find((key) => key.value === repeatType)?.label ?? t('everyRepeat'),
    [repeatType],
  );
  const [isOpenCollectionDetail, setIsOpenCollectionDetail] = useState(false);
  const [currentCollectionIndex, setCurrentCollectionIndex] = useState(-1);

  const currentCollectionItem = useMemo(
    () => collectionList[currentCollectionIndex],
    [currentCollectionIndex, collectionList],
  );
  const monthPeriod = useMemo(() => {
    const list = collectionList.map((value) => {
      const startedTime = value?.startedTime;
      const month = dayjs(startedTime).format('MMM').replace(/^0/, '');
      const year = dayjs(startedTime).format(t('YYYYFormat'));
      const key = dayjs(startedTime).format('YYYY-MM');

      return { month, year, key };
    });

    return uniqBy(list, 'key');
  }, [collectionList]);

  // 请求历史会议集合
  const requestCollectionList = async () => {
    const res = await getHistoryMeetingCollectionList({ roomId });

    if (isSuccessCode(res?.code)) {
      setCollectionList(res.data.list);
    }
  };

  useEffect(() => {
    if (roomId && isOpenCollectionList) {
      requestCollectionList();
    }
  }, [roomId, isOpenCollectionList]);

  return (
    <>
      <Drawer
        onClose={() => setIsOpenCollectionList(false)}
        open={isOpenCollectionList}
        styles={{
          mask: { opacity: 0 },
          content: { backgroundColor: '#f2f3f5' },
          wrapper: { boxShadow: 'none' },
          body: { padding: 0, margin: 0 },
        }}
        title={
          <div className='flex items-center justify-center'>
            <span className='text-[18px]'>{roomName}</span>
          </div>
        }
        width={390}
      >
        <div className='mx-3 py-4'>
          <div className='text-[13px] text-[rgba(19,20,21,.4)]'>
            <span>{t('meetingRepeatInfo', { repeatTypeName: tipText, meetingCount: collectionList.length })}</span>
          </div>
        </div>
        <div className='mx-3 overflow-hidden rounded-xl bg-[#fff]'>
          {monthPeriod.map((value, idx) => {
            const currentList = collectionList.filter((val) => dayjs(val?.startedTime).format('YYYY-MM') === value.key);

            return (
              <Fragment key={value.key}>
                <div className='flex items-center justify-between px-3 pt-3' key={value.key}>
                  <span className='text-[16px] font-medium text-[#131415]'>{value.month}</span>
                  <span className='text-[13px] text-[rgba(19,20,21,.4)]'>{value.year}</span>
                </div>
                {currentList.map((val) => {
                  return (
                    <Item
                      collectionList={collectionList}
                      index={collectionList.findIndex((key) => key?.startedTime === val?.startedTime)}
                      key={val?.startedTime}
                      setCurrentCollectionIndex={setCurrentCollectionIndex}
                      setIsOpenCollectionDetail={setIsOpenCollectionDetail}
                    />
                  );
                })}
              </Fragment>
            );
          })}
        </div>
      </Drawer>
      <MeetingDetail
        isOpen={isOpenCollectionDetail}
        roomId={currentCollectionItem?.roomId ?? ''}
        roomSid={currentCollectionItem?.roomSid ?? ''}
        setIsOpen={setIsOpenCollectionDetail}
      />
    </>
  );
}

export default App;
